import { Fragment, useMemo } from 'react';

import {
  alpha,
  Box,
  Chip,
  DialogContent,
  DialogTitle,
  Grid,
  GridProps,
  Stack,
  Typography,
} from '@mui/material';

import { ApplyRemoveAlgorithmResponseData, ENTITY_TYPE } from '../../types';

import { CircleCheck, TriangleAlert } from '@/shared/components/icons';
import { Tooltip } from '@/shared/components/tooltip';

interface ApplyRemoveAlgorithmStatusProps {
  data: ApplyRemoveAlgorithmResponseData;
}

const commonGridItemProps: GridProps = {
  px: 3,
  sx: { borderBottom: '1px solid', borderColor: 'divider' },
};

const entityTypeDisplayNames = {
  [ENTITY_TYPE.ADVERTISER]: 'advertiser',
  [ENTITY_TYPE.CAMPAIGN]: 'campaign',
  [ENTITY_TYPE.META_CAMPAIGN]: 'campaign',
  [ENTITY_TYPE.ADGROUP]: 'ad group',
  [ENTITY_TYPE.ADSET]: 'ad set',
  [ENTITY_TYPE.INSERTION_ORDER]: 'insertion order',
};

const ApplyRemoveAlgorithmStatus = ({
  data,
}: ApplyRemoveAlgorithmStatusProps) => {
  const isSuccess = data.status === 'success';
  const isError = data.status === 'error';
  const isPartailSuccess = data.status === 'partial_success';
  const isApplyAlgorithmAction = data.action === 'apply';

  const description = useMemo(() => {
    const totalEntities = data.results.length;
    const totalAlgorithms = data.results[0].statuses.length;

    const algorithmText = `${totalAlgorithms.toString()} algorithm${totalAlgorithms > 1 ? 's' : ''}`;
    const entityText = `${entityTypeDisplayNames[data.entityType]}${totalEntities > 1 ? 's' : ''}`;

    let description = '';
    if (isError) {
      description = `We couldn't process your request for any ${entityTypeDisplayNames[data.entityType]}. Please review the status below to see what went wrong.`;
    } else {
      description = `Your request to ${data.action} ${algorithmText} ${isApplyAlgorithmAction ? 'to' : 'from'} ${totalEntities.toString()} ${entityText} has been submitted.`;
      if (isPartailSuccess) {
        description += ` Some ${entityTypeDisplayNames[data.entityType]}s couldn't be processed successfully. Please review the status below.`;
      }
    }

    return description;
  }, [
    data.action,
    data.entityType,
    data.results,
    isError,
    isPartailSuccess,
    isApplyAlgorithmAction,
  ]);

  return (
    <>
      <DialogTitle id='apply-remove-algorithm-dialog'>
        <Typography
          variant='h6'
          component='span'
          fontWeight={600}
          display='block'
          color='text.primary'
        >
          {isApplyAlgorithmAction
            ? 'Application Request Submitted'
            : 'Removal Request Submitted'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0, pt: 3, pb: 5 }}>
        <Stack
          direction='row'
          alignItems='flex-start'
          gap={2}
          p={2}
          sx={{
            border: '1px solid',
            borderColor: (theme) =>
              isSuccess
                ? theme.palette.success.main
                : theme.palette.warning.main,
            borderRadius: 1,
          }}
        >
          <Stack
            alignItems='center'
            justifyContent='center'
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: (theme) =>
                isSuccess
                  ? theme.palette.success[50]
                  : theme.palette.warning[50],
            }}
          >
            {isSuccess ? (
              <CircleCheck color='success' fontSize='small' />
            ) : (
              <TriangleAlert color='warning' fontSize='small' />
            )}
          </Stack>
          <Stack>
            <Typography
              variant='body2'
              fontWeight={600}
              color={isSuccess ? 'success.dark' : 'warning.dark'}
            >
              {isSuccess
                ? 'Request Submitted'
                : isPartailSuccess
                  ? `Some ${entityTypeDisplayNames[data.entityType]}s couldn't be processed`
                  : `No ${entityTypeDisplayNames[data.entityType]} processed`}
            </Typography>
            <Typography variant='body3' color='text.secondary' pt={0.5}>
              {description}
            </Typography>
          </Stack>
        </Stack>

        <Box pt={3}>
          <Grid container>
            <Grid size={5} py={1.5} {...commonGridItemProps}>
              <Typography variant='body3' textTransform='capitalize'>
                {entityTypeDisplayNames[data.entityType]} Name
              </Typography>
            </Grid>
            <Grid size={3} py={1.5} {...commonGridItemProps}>
              <Typography variant='body3'>Algorithms</Typography>
            </Grid>
            <Grid size={4} py={1.5} {...commonGridItemProps}>
              <Typography variant='body3'>Status</Typography>
            </Grid>

            {data.results.map((result) => (
              <Fragment key={result.id}>
                <Grid size={5} py={2} {...commonGridItemProps}>
                  <Tooltip
                    title={result.name}
                    placement='top-start'
                    arrow={false}
                  >
                    <Typography
                      variant='body2'
                      whiteSpace='nowrap'
                      textOverflow='ellipsis'
                      overflow='hidden'
                    >
                      {result.name}
                    </Typography>
                  </Tooltip>
                </Grid>

                <Grid size={3} py={2} {...commonGridItemProps}>
                  <Stack
                    gap={0.5}
                    alignItems='flex-start'
                    justifyContent='center'
                  >
                    {result.statuses.map((status) => (
                      <Chip
                        key={status.id}
                        label={`${status.algorithmName} ${status.version ? `(${status.version})` : ''}`}
                        sx={{
                          color: status.color,
                          backgroundColor: alpha(status.color, 0.06),
                        }}
                        size='small'
                      />
                    ))}
                  </Stack>
                </Grid>

                <Grid size={4} py={2} {...commonGridItemProps}>
                  <Stack gap={0.5} alignItems='flex-start'>
                    {result.statuses.map((status) => (
                      <Chip
                        key={status.id}
                        label={status.statusMessage}
                        size='small'
                        sx={{
                          color: (theme) =>
                            status.status === 'Success'
                              ? theme.palette.success.main
                              : theme.palette.warning.main,
                          backgroundColor: (theme) =>
                            status.status === 'Success'
                              ? alpha(theme.palette.success.main, 0.1)
                              : alpha(theme.palette.warning.main, 0.1),
                        }}
                      />
                    ))}
                  </Stack>
                </Grid>
              </Fragment>
            ))}
          </Grid>
        </Box>
      </DialogContent>
    </>
  );
};

export default ApplyRemoveAlgorithmStatus;
